import { BaseERC20Factory } from '@arcxgame/contracts/dist/src/typings'
import { quickWeiValuePow } from '@arcxmoney/arcx-core'
import { getErc20Details, poll, useAsync, useErc20Details } from '@arcxmoney/arcx-react'
import { BigNumber, constants } from 'ethers'
import humanFormat from 'human-format'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useHistory } from 'react-router'
import { getConfigForNetwork } from 'src/config'
import { ArcContext } from 'src/contexts/ArcContext'
import { getReadableNumber } from 'src/helpers/getReadableNumber'
import { useSaleEligibility } from 'src/hooks'
import { getErc20Balance } from 'src/hooks/useErc20Balance'
import { CurveService } from 'src/services/CurveService'
import { Synth } from 'src/services/GraphService'
import { formatBigNumber } from 'src/utils/format/formatBigNumber'
import { DashboardItem } from '../DashboardItem'
import { Spinner } from '../Spinner'
import { AssetLocked } from './AssetLocked'
import './Dashboard.scss'

interface IProps {
  synths: Synth[]
}

export function Dashboard({ synths }: IProps) {
  const { arc, getPrice, prices, savings, network, isPlaynetActivated } = useContext(ArcContext)

  const [curveLiquidity, setCurveLiquidity] = useState<BigNumber>(BigNumber.from(0))

  const phase1 = useSaleEligibility(1)
  const phase2 = useSaleEligibility(2)

  const currencyInfo = useErc20Details(phase2.currency, arc?.signer)

  const phase1Eligible = phase1.participant.allocation.gt(BigNumber.from(0))
  const phase2Eligible = phase2.participant.allocation.gt(BigNumber.from(0))

  const history = useHistory()
  const [assetsLocked] = useAsync(async () => {
    if (!arc?.signer || prices.length < synths.length) {
      return undefined
    }
    const assetsLocked = []
    for (const synth of synths) {
      const collateralContract = BaseERC20Factory.connect(synth.collateralAddress, arc.signer)
      const lockedAmount = await getErc20Balance(
        collateralContract,
        arc.signer,
        synth.syntheticAddress,
      )
      const collateralDetails = await getErc20Details(collateralContract)
      const price = getPrice(synth.oracle)
      const lockedValue = Number(
        formatBigNumber(
          lockedAmount.mul(price).div(constants.WeiPerEther),
          collateralDetails.decimals,
        ),
      )
      assetsLocked.push({
        lockedValue,
        component: (
          <AssetLocked
            key={synth.id}
            synth={synth}
            lockedAmount={lockedAmount}
            lockedValue={lockedValue}
            collateralDetails={collateralDetails}
          />
        ),
      })
    }
    return assetsLocked
  }, [arc?.signer, prices])

  const curveService = useMemo(() => {
    if (!arc || !network) return

    const config = getConfigForNetwork(network, isPlaynetActivated)

    if (!config.curvePool) return

    return new CurveService(arc.signer, config.curvePool)
  }, [arc, network, isPlaynetActivated])

  useEffect(() => {
    return poll(async () => {
      const poolInfo = await curveService?.getPoolInfo()

      if (!poolInfo) return

      const totalCurveLiquidity = poolInfo.totalLiquidity

      setCurveLiquidity(totalCurveLiquidity)
    }, 10000)
  }, [curveService])

  const [uniswapPoolRatioInfo] = useAsync(
    async () => arc?.getUniswapRatioDetails('0x1bcce9e2fd56e8311508764519d28e6ec22d4a47'),
    [arc],
  )

  if (!assetsLocked) {
    return <Spinner />
  }

  let savingsRateValue: string

  if (savings[0]) {
    savingsRateValue = getReadableNumber(
      quickWeiValuePow(
        constants.WeiPerEther.add(savings[0].savingsRate),
        BigNumber.from(364 * 24 * 60 * 60),
        300,
      )
        .sub(constants.WeiPerEther)
        .mul(100),
      2,
    )

    savingsRateValue += '%'
  } else {
    savingsRateValue = 'N/A'
  }

  if (phase2.hardcap && phase2.totalRaised) {
    console.log(phase2.totalRaised.toString())
    console.log(phase2.hardcap.toString())
    console.log(currencyInfo)
    console.log(formatBigNumber(phase2.totalRaised, currencyInfo?.decimals))
  }

  const onPhase1EligibleClicked = () => {
    history.push('/phase1-sale')
  }

  const onPhase2EligibleClicked = () => {
    history.push('/phase2-sale')
  }

  const phase1Button = (
    <button
      className={'dashboard__claim-btn ' + (phase1Eligible ? '' : 'dashboard__claim-btn--red')}
      disabled={!phase1Eligible}
      onClick={onPhase1EligibleClicked}
    >
      {phase1Eligible === null ? '...' : phase1Eligible ? 'Claim' : 'No'}
    </button>
  )

  const phase2Button = (
    <button
      className={'dashboard__claim-btn ' + (phase2Eligible ? '' : 'dashboard__claim-btn--red')}
      disabled={!phase2Eligible}
      onClick={onPhase2EligibleClicked}
    >
      {phase2Eligible === null ? '...' : phase2Eligible ? 'Claim' : 'No'}
    </button>
  )

  const claimButton = (
    <button className={'dashboard__claim-btn'} onClick={() => history.push('/token-claim')}>
      Claim
    </button>
  )

  const claimButtonPhase2 = (
    <button className={'dashboard__claim-btn'} onClick={() => history.push('/token-claim-2')}>
      Claim
    </button>
  )

  const assetsTVL = assetsLocked
    .map(({ lockedValue }) => lockedValue)
    .reduce((prev, cur) => prev + cur, 0)
  const crvLiquidity = Number(formatBigNumber(curveLiquidity))
  const fullTVL = assetsTVL + crvLiquidity

  return (
    <>
      <div className="dashboard__items-container">
        <DashboardItem
          title="STABLEx"
          rows={[
            {
              property: uniswapPoolRatioInfo?.name,
              value: getReadableNumber(uniswapPoolRatioInfo?.ratio),
            },
            {
              property: 'TVL Total',
              value: `$${humanFormat(fullTVL)}`,
              tooltip: `Assets TVL ($${humanFormat(
                assetsTVL,
              )}) + Liquidity Pool Depth ($${humanFormat(crvLiquidity)})`,
            },
            {
              property: 'Savings Rate',
              value: savingsRateValue,
            },
          ]}
        />
        <DashboardItem
          title="Token Sale"
          rows={[
            {
              property: 'Phase 1 Eligible',
              value: phase1Button,
            },
            {
              property: 'Phase 2 Eligible',
              value: phase2Button,
            },
            {
              property: 'Token Claim',
              value: claimButton,
            },
            {
              property: 'Token Claim Phase 2',
              value: claimButtonPhase2,
            },
          ]}
        />
      </div>
      <h3>Assets Locked Up</h3>
      <div className="dashboard__assets-locked">
        {assetsLocked.map(({ component }) => component)}
      </div>
    </>
  )
}
